import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';

/** Folha do submenu: um filtro ou uma tela dentro de uma frente do módulo. */
export interface SidebarSubFilho {
    id: string;
    label: string;
    badge?: string;
}

/**
 * Entrada do submenu. `filhos` aninha **um único nível** — e o tipo diz isso,
 * porque a implementação rende exatamente um. O `.d.ts` antigo não declarava
 * `filhos`, embora `Sidebar.jsx` já o lesse: contrato incompleto, não recurso
 * novo.
 */
export interface SidebarSubItem extends SidebarSubFilho {
    filhos?: SidebarSubFilho[];
}

export interface SidebarModule {
    id: string;
    label: string;
    icon: string;
    badge?: string;
    /** Entradas do painel flutuante (modo rail) ou da árvore recuada (expandido). */
    sub?: SidebarSubItem[];
}

/** Rótulo de agrupamento. Vira divisória no modo rail. */
export interface SidebarSection {
    section: string;
}

export type SidebarEntry = SidebarModule | SidebarSection;

function ehSecao(entrada: SidebarEntry): entrada is SidebarSection {
    return 'section' in entrada;
}

export interface SidebarProps {
    /** Módulos em ordem. Entradas `{ section }` os agrupam. */
    items?: SidebarEntry[];
    activeId?: string;
    /** Entrada selecionada dentro do submenu do módulo aberto. */
    activeSubId?: string;
    onSelect?: (id: string) => void;
    /**
     * Sub-opção escolhida, COM o módulo a que ela pertence. O módulo vem junto porque
     * no painel flutuante a sub-opção é de um módulo que ainda não é o da tela: montar
     * o endereço com o módulo atual levava a "não existe".
     */
    onSelectSub?: (id: string, modulo: string) => void;
    /** Nome da navegação para leitores de tela. A página tem mais de um `nav`. */
    ariaLabel?: string;
    /** Marca-d'água do modo expandido. */
    logoSrc?: string;
    /**
     * Variante da logo para o tema escuro. As duas imagens ficam no DOM e o CSS
     * escolhe (ver `tokens/base.css`) — trocar o `src` em JS pediria que todo
     * componente com logo reagisse ao tema, e a troca piscaria.
     */
    logoDarkSrc?: string;
    /** Marca "cc" do modo rail. */
    logoMarkSrc?: string;
    /** "rail" = 56px de ícones + submenu flutuante (padrão do produto); "expanded" = lista rotulada de 236px. */
    variant?: 'rail' | 'expanded';
    /** Apelido legado de variant="rail". */
    collapsed?: boolean;
    /** Forneça para renderizar o botão de expandir/recolher. Vire `variant` no manipulador. */
    onToggleVariant?: () => void;
    footer?: ReactNode;
    style?: CSSProperties;
}

/** Navegação global da Fórmula MED. */
export function Sidebar({
    items = [], activeId, activeSubId, onSelect, onSelectSub, ariaLabel = 'Navegação principal',
    logoSrc, logoDarkSrc, logoMarkSrc, variant = 'expanded', collapsed = false, onToggleVariant, footer, style,
}: SidebarProps) {
    const rail = variant === 'rail' || collapsed;
    const [openId, setOpenId] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement | null>(null);
    /* Um ref por módulo para devolver o foco ao gatilho quando o painel fecha
       por Escape — sem isso o foco cai no início do documento. */
    const botoesRef = useRef<Record<string, HTMLButtonElement | null>>({});

    useEffect(() => {
        if (!openId) return;
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpenId(null); };
        const k = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            botoesRef.current[openId]?.focus();
            setOpenId(null);
        };
        document.addEventListener('mousedown', h);
        document.addEventListener('keydown', k);
        return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
    }, [openId]);

    const modules = items.filter((i): i is SidebarModule => !ehSecao(i));
    const open = modules.find((m) => m.id === openId);
    const painelId = openId ? `vec-sidebar-${openId}` : undefined;

    const pick = (item: SidebarModule) => {
        if (rail && item.sub && item.sub.length) { setOpenId((o) => (o === item.id ? null : item.id)); return; }
        setOpenId(null);
        onSelect?.(item.id);
    };

    return (
        <div ref={ref} style={{ position: 'relative', display: 'flex', flex: '0 0 auto', height: '100%', ...style }}>
            <nav
                aria-label={ariaLabel}
                style={{
                    width: rail ? 56 : 236, flex: '0 0 auto', height: '100%', display: 'flex', flexDirection: 'column',
                    /* Navegação em Deep Clinic nos dois temas (Fórmula MED, 15/09/2026). */
                    background: 'var(--nav-bg)', borderRight: '1px solid var(--nav-border)',
                }}
            >
                <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: rail ? 'center' : 'space-between', gap: 10, padding: rail ? 0 : '0 12px 0 16px', borderBottom: '1px solid var(--nav-border)' }}>
                    {rail
                        ? (logoMarkSrc
                            ? <img src={logoMarkSrc} alt="Fórmula MED" style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'contain' }} />
                            /* Monograma do design system (GlobalRail): F em branco clínico, M em
                               verde. 56 px não comportam o logotipo inteiro sem quebrar a marca. */
                            : (
                                <span role="img" aria-label="Fórmula MED" style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, letterSpacing: '-.02em', color: 'var(--fm-clinical-white)' }}>
                                    F<span style={{ color: 'var(--fm-formula-green)' }}>M</span>
                                </span>
                            ))
                        : (logoSrc
                            ? (
                                <>
                                    <img src={logoSrc} alt="Fórmula MED" data-vec-logo="light" style={{ height: 22, maxWidth: 160, objectFit: 'contain', objectPosition: 'left' }} />
                                    {/* A variante escura fica com alt vazio: as duas
                                        imagens coexistem no DOM e só o CSS escolhe, então
                                        dois `alt` anunciariam a marca em dobro se alguma
                                        vez as duas ficassem visíveis. */}
                                    {logoDarkSrc && <img src={logoDarkSrc} alt="" data-vec-logo="dark" style={{ height: 22, maxWidth: 160, objectFit: 'contain', objectPosition: 'left' }} />}
                                </>
                            )
                            : <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, color: 'var(--nav-text)' }}>Fórmula MED</span>)}
                    {onToggleVariant && !rail && <ToggleButton rail={rail} onClick={onToggleVariant} />}
                </div>
                {onToggleVariant && rail && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 2px' }}>
                        <ToggleButton rail={rail} onClick={onToggleVariant} />
                    </div>
                )}

                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: rail ? '8px 0' : '10px 8px', display: 'flex', flexDirection: 'column', gap: rail ? 3 : 1, alignItems: rail ? 'center' : 'stretch' }}>
                    {items.map((item, i) => (ehSecao(item)
                        ? (rail
                            ? (i > 0 ? <span key={`s${i}`} aria-hidden="true" style={{ width: 24, height: 1, background: 'var(--nav-border)', margin: '7px 0' }} /> : null)
                            : <div key={item.section} style={{ padding: '14px 10px 5px', fontSize: 11, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--nav-text-muted)' }}>{item.section}</div>)
                        : (
                            <Fragment key={item.id}>
                                <SidebarItem
                                    item={item} rail={rail} active={item.id === activeId} open={item.id === openId}
                                    painelId={item.id === openId ? painelId : undefined}
                                    onPick={pick}
                                    botaoRef={(el) => { botoesRef.current[item.id] = el; }}
                                />
                                {!rail && item.sub && item.id === activeId && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, margin: '2px 0 6px', paddingLeft: 10, borderLeft: '1px solid var(--nav-border)', marginLeft: 14 }}>
                                        <SubTree
                                            sub={item.sub} modulo={item.id} activeSubId={activeSubId}
                                            onSelect={onSelect} onSelectSub={onSelectSub}
                                        />
                                    </div>
                                )}
                            </Fragment>
                        )))}
                </div>

                {footer && <div style={{ padding: rail ? 8 : 10, borderTop: '1px solid var(--nav-border)' }}>{footer}</div>}
            </nav>

            {rail && open && open.sub && (
                <Flyout
                    id={painelId} modulo={open} ativo={open.id === activeId} activeSubId={activeSubId}
                    onEscolher={(subId) => { setOpenId(null); if (onSelectSub) onSelectSub(subId, open.id); else onSelect?.(open.id); }}
                />
            )}
        </div>
    );
}

interface FlyoutProps {
    id?: string;
    modulo: SidebarModule;
    ativo: boolean;
    activeSubId?: string;
    onEscolher: (subId: string) => void;
}

/** Painel flutuante do modo rail: o submenu do módulo aberto. */
function Flyout({ id, modulo, ativo, activeSubId, onEscolher }: FlyoutProps) {
    const sub = modulo.sub ?? [];
    const primeiro = sub[0]?.id;

    const linha = (sb: SidebarSubFilho, filho: boolean) => {
        const on = ativo && (activeSubId ? sb.id === activeSubId : sb.id === primeiro);
        return (
            <button
                key={sb.id} type="button"
                aria-current={on ? 'page' : undefined}
                onClick={() => onEscolher(sb.id)}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = 'var(--nav-hover)'; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                style={{
                    display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: filho ? 30 : 34,
                    padding: filho ? '5px 11px 5px 22px' : '7px 11px',
                    border: 0, borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left',
                    background: on ? 'var(--nav-active-bg)' : 'transparent',
                    color: on ? 'var(--nav-active-fg)' : filho ? 'var(--nav-text-muted)' : 'var(--nav-text)',
                    fontFamily: 'var(--font-ui)', fontSize: filho ? 12.5 : 13, fontWeight: on ? 600 : filho ? 500 : 600,
                    transition: 'var(--transition-control)',
                }}
            >
                {filho && <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'currentColor', opacity: .55, flex: '0 0 auto' }} />}
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sb.label}</span>
                {sb.badge && <span style={{ fontSize: 11, fontWeight: 600, color: on ? 'var(--nav-active-fg)' : 'var(--nav-selected-fg)', background: on ? 'rgba(5,29,26,.14)' : 'var(--nav-selected-bg)', borderRadius: 'var(--radius-pill)', padding: '1px 7px' }}>{sb.badge}</span>}
            </button>
        );
    };

    return (
        <div
            id={id} role="group" aria-label={modulo.label}
            style={{
                position: 'absolute', left: 56, top: 0, width: 232, height: '100%', zIndex: 30,
                background: 'var(--nav-panel)', borderRight: '1px solid var(--nav-border)',
                boxShadow: 'var(--shadow-lg)', padding: 10, display: 'flex', flexDirection: 'column', gap: 2,
                animation: 'vec-flyout var(--dur-base) var(--ease-entrance)',
            }}
        >
            <p style={{ margin: 0, padding: '9px 10px 12px', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, letterSpacing: 'var(--tracking-snug)', color: 'var(--fm-clinical-white)' }}>{modulo.label}</p>
            {sub.map((sb) => (sb.filhos ? (
                <Fragment key={sb.id}>
                    {linha(sb, false)}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 4 }}>
                        {sb.filhos.map((f) => linha(f, true))}
                    </div>
                </Fragment>
            ) : linha(sb, false)))}
            <style>{'@keyframes vec-flyout{from{opacity:0;transform:translateX(-6px)}to{opacity:1;transform:none}}'}</style>
        </div>
    );
}

interface SubTreeProps {
    sub?: SidebarSubItem[];
    modulo: string;
    activeSubId?: string;
    onSelect?: (id: string) => void;
    onSelectSub?: (id: string, modulo: string) => void;
}

/**
 * Subitens com um nível de aninhamento. Um grupo (item com `filhos`) é
 * clicável — leva à visão sem filtro — e os filhos aparecem indentados como
 * filtros daquela visão. Sem isso, um módulo com três frentes e sete telas
 * viraria uma lista plana onde nada indica o que pertence a quê.
 */
function SubTree({ sub = [], modulo, activeSubId, onSelect, onSelectSub }: SubTreeProps) {
    const primeiro = sub[0]?.id;
    const escolher = (id: string) => { if (onSelectSub) onSelectSub(id, modulo); else onSelect?.(modulo); };

    const linha = (sb: SidebarSubFilho, filho: boolean) => {
        const on = activeSubId ? sb.id === activeSubId : sb.id === primeiro;
        return (
            <button
                key={sb.id} type="button" onClick={() => escolher(sb.id)}
                /* `aria-current="page"`, não `"true"`: o valor genérico só diz
                   "atual"; `page` diz atual DENTRO da navegação, que é o que a
                   entrada selecionada é. */
                aria-current={on ? 'page' : undefined}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 30,
                    padding: filho ? '5px 10px 5px 18px' : '5px 10px',
                    border: 0, borderRadius: 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left',
                    background: on ? 'var(--nav-selected-bg)' : 'transparent',
                    /* Subitem ativo fica em verde translúcido com tinta verde; módulo ativo é
                       sólido — a diferença marca a hierarquia em vez de competir. */
                    color: on ? 'var(--nav-selected-fg)' : filho ? 'var(--nav-text-muted)' : 'var(--nav-text)',
                    fontFamily: 'var(--font-ui)', fontSize: filho ? 12 : 12.5, fontWeight: on ? 600 : filho ? 500 : 600,
                    transition: 'var(--transition-control)',
                }}
            >
                {filho && <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'currentColor', opacity: .55, flex: '0 0 auto' }} />}
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sb.label}</span>
                {sb.badge && <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--nav-selected-fg)', background: 'var(--nav-selected-bg)', borderRadius: 'var(--radius-pill)', padding: '1px 6px' }}>{sb.badge}</span>}
            </button>
        );
    };

    return (
        <>
            {sub.map((sb) => (sb.filhos ? (
                <Fragment key={sb.id}>
                    {linha(sb, false)}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 4 }}>
                        {sb.filhos.map((f) => linha(f, true))}
                    </div>
                </Fragment>
            ) : linha(sb, false)))}
        </>
    );
}

function ToggleButton({ rail, onClick }: { rail: boolean; onClick: () => void }) {
    const [hover, setHover] = useState(false);
    const label = rail ? 'Expandir menu' : 'Recolher menu';
    return (
        <button
            type="button" onClick={onClick} aria-label={label} title={label}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                width: rail ? 36 : 28, height: rail ? 30 : 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 0, borderRadius: 'var(--radius-xs)', cursor: 'pointer',
                background: hover ? 'var(--nav-hover)' : 'transparent',
                color: 'var(--nav-text-muted)', transition: 'var(--transition-control)', flex: '0 0 auto',
            }}
        >
            <Icon name={rail ? 'panel-left-open' : 'panel-left-close'} size={16} />
        </button>
    );
}

interface SidebarItemProps {
    item: SidebarModule;
    rail: boolean;
    active: boolean;
    open: boolean;
    painelId?: string;
    onPick: (item: SidebarModule) => void;
    botaoRef: (el: HTMLButtonElement | null) => void;
}

function SidebarItem({ item, active, open, rail, painelId, onPick, botaoRef }: SidebarItemProps) {
    const [hover, setHover] = useState(false);
    const lit = active || open;
    /* No rail, o módulo com submenu ABRE um painel em vez de navegar — e é isso
       que `aria-haspopup`/`aria-expanded` anunciam. Sem eles, o botão prometia
       navegação e entregava um painel silencioso. */
    const abrePainel = rail && !!item.sub?.length;
    return (
        <button
            ref={botaoRef} type="button" title={rail ? item.label : undefined}
            /* No rail some o rótulo e o badge vira um ponto sem texto: os dois
               entram no nome acessível, senão o botão fica só com o ícone e o
               "3" do badge desaparece para quem ouve. */
            aria-label={rail ? `${item.label}${item.badge ? `, ${item.badge}` : ''}` : undefined}
            /* O módulo ativo é marcado mesmo quando abre painel: `aria-current`
               diz em que seção a pessoa está, e `aria-haspopup` diz o que o
               botão faz — são coisas diferentes e não se anulam. */
            aria-current={active ? 'page' : undefined}
            aria-haspopup={abrePainel ? 'menu' : undefined}
            aria-expanded={abrePainel ? open : undefined}
            aria-controls={abrePainel && open ? painelId : undefined}
            onClick={() => onPick(item)}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 10,
                width: rail ? 36 : '100%', height: rail ? 36 : 34,
                padding: rail ? 0 : '0 10px', justifyContent: rail ? 'center' : 'flex-start',
                border: 0, borderRadius: rail ? 'var(--radius-sm)' : 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left',
                /* Ativo é Formula Green sólido com tinta Deep Clinic (11,9:1): o sólido
                   separa melhor o item ativo numa nav de 16 módulos, e o verde é
                   justamente o de seleção no design system. */
                background: lit ? 'var(--nav-active-bg)' : hover ? 'var(--nav-hover)' : 'transparent',
                color: lit ? 'var(--nav-active-fg)' : 'var(--nav-text)',
                fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: lit ? 600 : 500,
                transition: 'var(--transition-control)', flex: '0 0 auto',
            }}
        >
            <Icon name={item.icon} size={17} />
            {!rail && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
            {!rail && item.badge && (
                <span
                    style={{
                        fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '1px 7px',
                        color: lit ? 'var(--nav-active-fg)' : 'var(--nav-selected-fg)',
                        background: lit ? 'rgba(5,29,26,.14)' : 'var(--nav-selected-bg)',
                    }}
                >{item.badge}</span>
            )}
            {rail && item.badge && <span aria-hidden="true" style={{ position: 'absolute', top: 3, right: 3, width: 6, height: 6, borderRadius: '50%', background: 'var(--fm-formula-green)' }} />}
        </button>
    );
}
